/* eslint-disable jsx-a11y/anchor-is-valid */
import dayjs from "dayjs";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { calculateHash } from "../components/utils/helper";
import { db } from "../config/firebaseConfig";
import JoinRoom from "./JoinRoom";
import PrismCard from "./PrismCard";

export const signTransaction = async (transaction, privateKey) => {
  // In real scenarios, use cryptographic methods for signing
  return `signed(${transaction.amount},${transaction.receiver},${privateKey})`;
};

const MainComponent = ({ copyToClipboard, createWallet, userData, user, logout }) => {
  const [endTime, setEndTime] = useState("");
  const [roomId, setRoomId] = useState(localStorage.getItem("roomId") || "");
  const [roomName, setRoomName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [role, setRole] = useState(() => localStorage.getItem("role") || "miner");

  useEffect(() => {
    if (!roomId) return;

    const roomRef = db.collection("rooms").doc(roomId);

    const checkEndTimeAndDeleteRoom = (roomData) => {
      if (!roomData) return;

      const endTime = new Date(roomData.endTime).getTime();
      const currentTime = new Date().getTime();

      if (currentTime > endTime) {
        roomRef
          .delete()
          .then(() => {
            toast.success("Room ended due to passing the end time.");
            setRoomId("");
            // setParticipants([]);
            localStorage.removeItem("roomId");
          })
          .catch((error) => {
            console.error("Error deleting room:", error);
            toast.error("Error deleting room.");
            localStorage.removeItem("roomId");
          });
      }
    };

    const unsubscribe = roomRef.onSnapshot(
      (roomSnapshot) => {
        if (roomSnapshot.exists) {
          const roomData = roomSnapshot.data();
          // setParticipants(roomData.participants || []);
          // setMiners(roomData.miners || []);
          setEndTime(roomData.endTime);

          checkEndTimeAndDeleteRoom(roomData);
        }
      },
      (error) => {
        console.error("Error fetching room details:", error);
        toast.error("Error fetching room details.");
      },
    );

    return () => unsubscribe();
  }, [roomId]);

  const handleCreateRoom = async () => {
    setIsLoading(true);
    try {
      const roomRef = await db.collection("rooms").add({
        endTime: endTime,
        createdBy: user.email,
        stakers: [],
        roomName: roomName,
        creatorAddress: userData.publicKey,
        createdAt: new Date(),
        participants: role === "participant" ? [userData.publicKey] : [],
        miners: role === "miner" ? [userData.publicKey] : [],
        roomType: "ASTRAMINER",
      });

      // create a initial transaction genisis block
      const block = {
        sender: userData.publicKey,
        senderEmail: user.email,
        roomName: roomName,
        receiver: userData.publicKey,
        timestamp: dayjs().unix(),
        status: "genesis",
        created_at: serverTimestamp(),
      };
      block.hash = calculateHash([], 0, block.timestamp);

      await setDoc(doc(db, "rooms", roomRef.id, "blocks", block.hash), block);
      setRoomId(roomRef.id);
      localStorage.setItem("roomId", roomRef.id);
      localStorage.setItem("role", role);
      toast.success("Room created successfully");
      window.location.href = "/joinedroom";
    } catch (error) {
      console.error("Error creating room:", error);
      setErrorMessage("Failed to create room. Please try again later.");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="min-h-screen  pt-6 pb-12 px-6 relative overflow-hidden">
      <div className="max-w-7xl mx-auto text-white">
        {/* Dashboard Overview Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-6">
            Dashboard Overview
          </h1>

          {/* Stats Cards Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {/* Balance Card */}
            <PrismCard className="bg-white/5 backdrop-blur-xl rounded-xl p-5 border border-white/10 relative shadow-2xl shadow-purple-500/10 before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/10 before:to-transparent before:opacity-50 before:pointer-events-none">
              <div className="flex items-start justify-between mb-3 relative z-10">
                <div className="flex items-center gap-2">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-purple-400"
                    />
                  </svg>
                  <span className="text-white/80 text-sm font-medium">Balance</span>
                </div>
                <button className="text-white/40 hover:text-white/60 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="6" r="1.5"/>
                    <circle cx="12" cy="12" r="1.5"/>
                    <circle cx="12" cy="18" r="1.5"/>
                  </svg>
                </button>
              </div>
              <h2 className="text-3xl font-bold text-white relative z-10">
                {userData?.balance?.toFixed(2) || "30.27"}
              </h2>
            </PrismCard>

            {/* Public Key Card */}
            <PrismCard className="bg-[#2A2F4A] backdrop-blur rounded-xl p-5 border border-[#3A3F5A] relative">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L2 7V12C2 17 6 21 12 22C18 21 22 17 22 12V7L12 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-blue-400"
                    />
                  </svg>
                  <span className="text-white/60 text-sm font-medium">Public Key</span>
                </div>
                <button className="text-white/40 hover:text-white/60 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="6" r="1.5"/>
                    <circle cx="12" cy="12" r="1.5"/>
                    <circle cx="12" cy="18" r="1.5"/>
                  </svg>
                </button>
              </div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-white truncate flex-1">
                  {userData?.publicKey?.slice(0, 7) || "AvesD"}...
                </h2>
                <button
                  onClick={(e) => copyToClipboard(e, userData?.publicKey)}
                  className="px-2.5 py-1 bg-[#7C3AED] hover:bg-[#6D28D9] rounded text-xs font-medium text-white transition-colors"
                >
                  Copy
                </button>
              </div>
            </PrismCard>

            {/* Private Key Card */}
            <PrismCard className="bg-[#2A2F4A] backdrop-blur rounded-xl p-5 border border-[#3A3F5A] relative">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="11"
                      width="18"
                      height="11"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-pink-400"
                    />
                    <path
                      d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-pink-400"
                    />
                  </svg>
                  <span className="text-white/60 text-sm font-medium">Private Key</span>
                </div>
                <button className="text-white/40 hover:text-white/60 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="6" r="1.5"/>
                    <circle cx="12" cy="12" r="1.5"/>
                    <circle cx="12" cy="18" r="1.5"/>
                  </svg>
                </button>
              </div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-white truncate flex-1">
                  {userData?.privateKey ? "••••••••" : "Bawyz"}...
                </h2>
                <button
                  onClick={(e) => copyToClipboard(e, userData?.privateKey)}
                  className="px-2.5 py-1 bg-[#7C3AED] hover:bg-[#6D28D9] rounded text-xs font-medium text-white transition-colors"
                >
                  Copy
                </button>
              </div>
            </PrismCard>

            {/* Profile Card */}
            <PrismCard className="bg-white/5 backdrop-blur-xl rounded-xl p-5 border border-white/10 relative shadow-2xl shadow-purple-500/10 before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/10 before:to-transparent before:opacity-50 before:pointer-events-none">
              <div className="flex items-center gap-3 relative z-10">
                <img
                  src={`https://api.dicebear.com/6.x/avataaars/svg?seed=${userData?.publicKey || user?.email}`}
                  alt="avatar"
                  className="w-12 h-12 rounded-full ring-2 ring-purple-500/30"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold text-sm truncate">
                    {userData?.email?.split("@")[0] || "David Do"}
                  </h3>
                  <p className="text-white/80 text-xs mt-0.5">
                    {userData?.balance?.toFixed(0) || "8"} Activity
                  </p>
                </div>
                <button
                  onClick={logout}
                  className="text-white/60 hover:text-red-400 hover:bg-red-400/10 p-2 rounded-lg transition-all duration-200"
                  title="Logout"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.59L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </PrismCard>
          </div>
        </div>

        {/* Create Room and Join Room Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Create Room Card */}
          <PrismCard className="bg-[#2A2F4A] backdrop-blur rounded-xl p-8 border border-[#3A3F5A]">
            <h3 className="text-xl font-semibold text-white mb-6">
              Create Room
            </h3>
            <div className="space-y-4">
              <input
                type="text"
                className="w-full bg-[#1F2437] border border-[#3A3F5A] rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#7C3AED] transition-colors"
                placeholder="Enter room name....."
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
              />
              <input
                type="datetime-local"
                className="w-full bg-[#1F2437] border border-[#3A3F5A] rounded-lg px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#7C3AED] transition-colors"
                placeholder="Enter date....."
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
              <div>
                <label className="block text-white/60 text-sm mb-2">Your Role</label>
                <select
                  className="w-full bg-[#1F2437] border border-[#3A3F5A] rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#7C3AED] transition-colors"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="miner">Miner</option>
                  <option value="participant">Participant</option>
                </select>
              </div>
              <button
                disabled={
                  isLoading ||
                  !endTime ||
                  !roomName ||
                  !userData?.isWalletCreated
                }
                onClick={handleCreateRoom}
                className="w-full px-6 py-3 bg-[#7C3AED] hover:bg-[#6D28D9] rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? "Creating..." : "Create room"}
              </button>
            </div>
          </PrismCard>

          {/* Join Room Card */}
          <PrismCard className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10 shadow-2xl shadow-purple-500/10 before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-br before:from-white/10 before:to-transparent before:opacity-50 before:pointer-events-none">
            <h3 className="text-xl font-semibold text-white mb-6 relative z-10">
              Join Room
            </h3>
            <div className="relative z-10">
              <JoinRoom />
            </div>
          </PrismCard>
        </div>

        {/* Rooms & Blocks Section */}
        <PrismCard className="bg-[#2A2F4A] backdrop-blur rounded-xl p-8 border border-[#3A3F5A]">
          <div className="mb-4">
            <h3 className="text-xl font-semibold text-white mb-2">
              Rooms & Blocks
            </h3>
            <p className="text-white/50 text-sm">
              Manage Rooms, View Blocks And Manage Transactions Here
            </p>
          </div>
          {!userData?.isWalletCreated && (
            <div className="text-center py-12">
              <p className="text-white/50 text-sm mb-4">
                Create a wallet to access rooms and blockchain features
              </p>
              <button
                onClick={createWallet}
                className="px-6 py-3 bg-[#7C3AED] hover:bg-[#6D28D9] rounded-lg font-medium text-white transition-colors"
              >
                Create Wallet
              </button>
            </div>
          )}
        </PrismCard>
      </div>
    </section>
  );
};

export default MainComponent;
